import { CustomTable } from 'components/CustomTable';
import Icon, { IconNames } from 'components/Icon';
import Loader from 'components/Loader';
import { has_permission } from 'config/permissions';
import colors from 'constants/colors';
import { payoutsAllTabColumns, payoutsColumns } from 'constants/tables';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import classNames from 'classnames';
import { FC, ReactNode, useEffect, useState } from 'react';
import { getActiveRole, getAdminsOBCheck, getSelectedRecipientId } from 'selectors/admins';
import { getPayoutsState } from 'selectors/payouts';
import { useLazyCheckOnboardingStatusQuery } from 'services/adminsApi';
import { useGetPayoutReportSignedUrlMutation } from 'services/payoutDownloadApi';
import { adminsOBCheckSuccess } from 'store/admins/slice';
import { getPayouts } from 'store/payouts/actions';
import { IPayoutsData, ReportType } from 'store/payouts/types';
import { toast } from 'react-toastify';
import { datadogLogs } from '@datadog/browser-logs';
import { DashboardScreen, trackEvent } from 'helpers/mixpanelEvents';

const SCREEN: DashboardScreen = 'payouts';

type ReportFilter = 'all' | 'weekly' | 'quarterly';

const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  STRIPE_PAYOUT: 'Weekly Summary',
  QUARTERLY_SUMMARY: 'Quarterly Summary',
};

const REPORT_TYPE_EVENT_VALUES: Record<ReportType, 'weekly' | 'quarterly'> = {
  STRIPE_PAYOUT: 'weekly',
  QUARTERLY_SUMMARY: 'quarterly',
};

type MappedPayoutRow = IPayoutsData & {
  date: string | null;
  type_label: string;
  actions: ReactNode;
};

// quarter_label format is "{year}Q{n}", e.g. "2025Q4" — year first, then Q + digit
function formatQuarterLabel(quarter_label: string): string {
  const year = quarter_label.slice(0, 4);
  const quarter = quarter_label.slice(5);
  return `Q${quarter} ${year}`;
}

const FILTER_OPTIONS: { label: string; value: ReportFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Quarterly', value: 'quarterly' },
];

export const Payouts: FC = () => {
  const dispatch = useAppDispatch();
  const [allData, setAllData] = useState<MappedPayoutRow[]>([]);
  const [activeFilter, setActiveFilter] = useState<ReportFilter>('all');
  const [payoutReportEligible, setPayoutReportEligible] = useState<boolean>(false);

  const activeRole = useAppSelector(getActiveRole);
  const payoutsState = useAppSelector(getPayoutsState);
  const recipient_id = useAppSelector(getSelectedRecipientId);
  const OBCheck = useAppSelector(getAdminsOBCheck);

  const [checkOnboardingStatus] = useLazyCheckOnboardingStatusQuery();
  const [getPayoutReportSignedUrl] = useGetPayoutReportSignedUrlMutation();

  const downloadPayoutReport = (report_id: string, report_type: ReportType) => {
    trackEvent('Payout Report Row Export Clicked', {
      screen: SCREEN,
      report_id,
      report_type: REPORT_TYPE_EVENT_VALUES[report_type],
    });
    getPayoutReportSignedUrl({
      recipient_id: String(recipient_id),
      report_id: report_id,
    })
      .unwrap()
      .then(signedUrl => {
        if (signedUrl) {
          const link = document.createElement('a');
          link.href = signedUrl;
          link.download = `payout_report_${report_id}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success('Payout report is ready in your "Downloads" folder', {
            autoClose: 5000,
            isLoading: false,
            closeOnClick: true,
            draggable: false,
          });
        } else {
          toast.error('Error getting URL for payout report', {
            autoClose: 5000,
            isLoading: false,
            closeOnClick: true,
            draggable: false,
          });
        }
      })
      .catch(() => {
        toast.error('Error downloading payout report', {
          autoClose: 5000,
          isLoading: false,
          closeOnClick: true,
          draggable: false,
        });
      });
  };

  // Fetch OBCheck when component mounts or recipient_id changes
  useEffect(() => {
    if (recipient_id) {
      checkOnboardingStatus(recipient_id)
        .unwrap()
        .then(data => {
          dispatch(adminsOBCheckSuccess(data));
        })
        .catch(error => {
          datadogLogs.logger.error('Failed to fetch onboarding status:', {}, error as Error);
        });
    }
  }, [recipient_id, dispatch, checkOnboardingStatus]);

  useEffect(() => {
    const payoutReportEligibleVal = !!(
      OBCheck &&
      OBCheck?.is_stripe_authorized &&
      !!recipient_id &&
      OBCheck?.onboarding_status !== 'NOT_STARTED'
    );

    setPayoutReportEligible(payoutReportEligibleVal);
  }, [recipient_id, OBCheck]);

  useEffect(() => {
    if (!payoutReportEligible || !recipient_id) {
      return;
    }
    dispatch(getPayouts(String(recipient_id), activeRole));
  }, [payoutReportEligible, recipient_id, activeRole, dispatch]);

  useEffect(() => {
    if (
      !payoutsState.isLoading &&
      payoutReportEligible &&
      payoutsState?.data &&
      Array.isArray(payoutsState.data)
    ) {
      const mapped = payoutsState.data
        .slice()
        .sort((a, b) => new Date(b.payout_date).getTime() - new Date(a.payout_date).getTime())
        .map(prop => ({
          ...prop,
          date:
            prop.report_type === 'QUARTERLY_SUMMARY' && prop.quarter_label
              ? formatQuarterLabel(prop.quarter_label)
              : dayjs(prop.payout_date).format('MMMM D, YYYY'),
          type_label: REPORT_TYPE_LABELS[prop.report_type] ?? prop.report_type,
          actions: (
            <button
              className="link-text inkBlue with-icon"
              onClick={() => downloadPayoutReport(prop.report_id, prop.report_type)}
              type="button"
            >
              <Icon name={IconNames.Export} color={colors.inkBlue} size={16} />
              Export
            </button>
          ),
        }));
      setAllData(mapped);
    }
  }, [payoutsState, payoutReportEligible]);

  let dataState = allData;
  if (activeFilter === 'weekly')
    dataState = allData.filter(row => row.report_type === 'STRIPE_PAYOUT');
  else if (activeFilter === 'quarterly')
    dataState = allData.filter(row => row.report_type === 'QUARTERLY_SUMMARY');

  return (
    <>
      {payoutsState.isLoading && <Loader type="dots" />}
      {!has_permission('PAYOUTS_ROUTE', 'R', activeRole) ? (
        <div className="content">
          <h1 className="title-permission">
            Sorry, you don&apos;t have permission to see this screen.
          </h1>
        </div>
      ) : (
        <>
          <div className="content content-mini payout-content">
            {payoutReportEligible ? (
              <div className="row recent-donations-table">
                <div className="col-lg-12">
                  <div className="date-filter-container">
                    {FILTER_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        className={classNames('date-filter-item', {
                          active: activeFilter === option.value,
                        })}
                        onClick={() => {
                          if (option.value !== activeFilter) {
                            trackEvent('Payouts Filter Changed', {
                              screen: SCREEN,
                              filter: option.value,
                            });
                          }
                          setActiveFilter(option.value);
                        }}
                        type="button"
                      >
                        <span className="oath-body inkBlue">{option.label}</span>
                      </button>
                    ))}
                  </div>
                  <CustomTable
                    key="payouts-list"
                    subtitle="For deposits with statement descriptor OATH.VOTE, reports can be exported below. For DEMOCRACY ENGINE, please login to Democracy Engine."
                    data={dataState}
                    columns={activeFilter === 'all' ? payoutsAllTabColumns : payoutsColumns}
                    withPagination
                    screen={SCREEN}
                  />
                </div>
              </div>
            ) : (
              <>
                <div className="row">
                  <div className="col-lg-12">
                    <div className="card">
                      <div className="card-body recipients-card-content">
                        <span className="h4-text">Payout Reports Unavailable</span>
                        <span className="body-text">
                          Sorry, payout reports are unavailable. Go back to the dashboard for more
                          information.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
};
